/**
 * Saved card the Become-a-Member checkout should have selected.
 *
 * "Click To Pay" stays disabled until a saved card is selected, and cards
 * saved during an earlier checkout often are not the customer's default, so
 * fall back to the first saved card instead of leaving nothing selected.
 *
 * @param options.paymentMethods - Saved cards for the customer
 * @param options.selected - Card currently selected in the form
 * @param options.defaultPaymentMethod - Customer's default card, if any
 * @param options.declinedPaymentMethodId - Card that was just declined
 * @returns {T | null} Card to select, or null when no saved card is usable
 */
export const resolveSelectedPaymentMethod = <
	T extends { id: string },
>(options: {
	paymentMethods: readonly T[];
	selected: T | null;
	defaultPaymentMethod?: T;
	declinedPaymentMethodId?: string;
}): T | null => {
	const {
		paymentMethods,
		selected,
		defaultPaymentMethod,
		declinedPaymentMethodId,
	} = options;

	if (selected && paymentMethods.some((method) => method.id === selected.id)) {
		return selected;
	}

	const usable = paymentMethods.filter(
		(method) => method.id !== declinedPaymentMethodId,
	);

	return (
		usable.find((method) => method.id === defaultPaymentMethod?.id) ??
		usable[0] ??
		null
	);
};
